import {
    colors,
    Jurisdiction,
    MapAreaLevel,
    MapInsightTile,
    ZOOM_THRESHOLDS,
} from '@hazadapt-git/public-core-base'
import bboxPolygon from '@turf/bbox-polygon'
import difference from '@turf/difference'
import { Feature, MultiPolygon, Polygon } from 'geojson'
import * as turf from '@turf/helpers'
import tinycolor from 'tinycolor2'
import {
    ExpressionSpecification,
    FillLayerSpecification,
    LineLayerSpecification,
} from 'mapbox-gl'

export const isPolygonTypeFeature = (feature: Feature): boolean =>
    feature.geometry.type === 'Polygon' ||
    feature.geometry.type === 'MultiPolygon'

export const interactiveMapLayers = Object.values(MapAreaLevel).map(
    (l) => `${l}-fill`
)

export const createJurisdictionMask = (
    jurisdiction?: Jurisdiction
): Feature<Polygon | MultiPolygon> | null => {
    if (!jurisdiction) return null
    const bboxPoly = bboxPolygon([-180, -90, 180, 90])
    const diff = difference(
        turf.featureCollection([
            bboxPoly,
            ...(jurisdiction.features.filter((f) =>
                isPolygonTypeFeature(f)
            ) as Feature<Polygon | MultiPolygon>[]),
        ])
    )
    return diff
}

export const fillConfigurator = (
    tiles: MapInsightTile[],
    level: MapAreaLevel,
    activeTheme: string
) => {
    const emptyColor = tinycolor(colors.grays.CUMULUS)
        .lighten(25)
        .setAlpha(0.67)
        .toRgbString()
    if (!tiles.some((tile) => tile.id.startsWith(level))) return emptyColor
    const matchExpression: ExpressionSpecification = ['match', ['get', 'id']]

    for (const tile of tiles) {
        if (!tile.id.startsWith(level)) continue
        const color =
            tile.value > 0
                ? tinycolor(activeTheme)
                      .lighten(tile.lightening)
                      .setAlpha(0.67)
                      .toRgbString()
                : emptyColor
        matchExpression.push(tile.id, color)
    }
    matchExpression.push('rgba(0,0,0,0)')

    return matchExpression
}

export const layerConfigurator = (
    tiles: MapInsightTile[],
    level: MapAreaLevel,
    numSelectedTiles: number
): Partial<FillLayerSpecification> | Partial<LineLayerSpecification> => {
    const filter = [
        'all',
        ['==', 'level', level],
        ['in', 'id', ...tiles.map((tile) => tile.id)],
    ]
    if (level === MapAreaLevel.STATE) {
        return {
            source: 'states',
            'source-layer': 'states',
            minzoom: ZOOM_THRESHOLDS.state, // absolute minimum zoom is 2
            maxzoom: 4,
            filter,
        }
    } else if (level === MapAreaLevel.COUNTY) {
        return {
            source: 'counties',
            'source-layer': 'counties',
            minzoom:
                ZOOM_THRESHOLDS[
                    numSelectedTiles > 0
                        ? MapAreaLevel.STATE
                        : MapAreaLevel.COUNTY
                ], // absolute minimum zoom is 2
            maxzoom: 8,
            filter,
        }
    } else {
        return {
            source: 'zips',
            'source-layer': 'zips',
            minzoom:
                ZOOM_THRESHOLDS[
                    numSelectedTiles > 0 ? MapAreaLevel.STATE : MapAreaLevel.ZIP
                ], // absolute minimum zoom is 2
            filter,
        }
    }
}
